/**
 * Landing v3 — "...I can always edit like an Agent!" (Figma 119:8431, user
 * 2026-09-29): a pretend chat with Ka that shows he is an AGENT — he changes
 * and makes the reader's material when asked, he does not only answer. Ka
 * floats into the card (its `KaSlot`, above his question "Want to be more specific?")
 * and says the line; his question streams from him the moment he lands.
 * Under it, the chat's own shortcut chips and composer, where a request
 * types itself, is wiped and the next one types in: five cards on one
 * concept of a lecture, longer cards, a topic to focus on, a mnemonic, a
 * quiz, an oral exam, notes (`landing-agent-requests`). Each
 * request lights the shortcut it belongs to, and a tap on a shortcut types
 * its request.
 *
 * The pieces are the chat's own DS pieces — `Chip`, `Button`, the composer's
 * frame (`CHAT_COMPOSER_FRAME_CLASS`) — not `ChatComposer` itself, which needs
 * the app's deck modal, dictation and analytics. Nothing here sends anything:
 * the composer is a picture, and screen readers get one plain sentence. Its
 * send and dictation buttons are the one way in (user 2026-09-30): they open
 * sign-up (the dashboard when signed in), captured as
 * `Homepage-Agent-Chat-Clicked`.
 *
 * Cheap: one timer a keystroke while the card is near the screen, nothing
 * while it is away; under reduced motion each request is simply there.
 */
import {
  Button,
  Chip,
  OVERLAY_DESC_CLASS,
  OVERLAY_SURFACE,
  surfaceShape,
  TIP_ARROW_SIZE,
  TipArrow,
} from '@flashka/shared/ui';
import { FlashcardIcon } from '@flashka/shared/ui/icons';

import { motion, useInView } from 'framer-motion';
import { AtSign, Mic, Plus, SendHorizontal } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useAuth } from '@/auth/better/session';
import { authDialogs } from '@/auth/better/store';
import { CHAT_COMPOSER_FRAME_CLASS } from '@/components/chat/chat-composer-frame';
import { cn } from '@/lib/utils';

import {
  AGENT_REQUESTS,
  AGENT_SHORTCUTS,
  AGENT_SOURCES,
  type AgentShortcutId,
  type AgentSource,
} from './landing-agent-requests';
import {
  TYPING_START,
  typingFrom,
  type TypingState,
  typingStep,
} from './landing-agent-typing';
import { GLASS_SHEET_CLASS } from './landing-chrome';
import { EASE_OUT, KA_SMALL, KaSlot, useKaLandedOn } from './landing-ka';
import { lineStreamSeconds, toWords, TriggeredLine } from './landing-lines';

/** The loop, one step per timer, only while `running`. */
function useTyping(lengths: readonly number[], running: boolean) {
  const [state, setState] = useState<TypingState>(TYPING_START);
  const lengthsRef = useRef(lengths);
  lengthsRef.current = lengths;
  useEffect(() => {
    if (!running) return;
    const { next, delay } = typingStep(state, lengthsRef.current);
    const id = window.setTimeout(() => setState(next), delay);
    return () => window.clearTimeout(id);
  }, [state, running]);
  return [state, setState] as const;
}

/** One word of Ka's question a beat; each fades and sharpens in (the oral
 *  exam's streamed caption). */
const QUESTION_WORD_BEAT = 0.09;
const QUESTION_LEAD = 0.1;
const QUESTION_WORD_FADE = 0.35;
/** Seconds Ka's question of `words` words takes to stream in. */
const questionSeconds = (words: number) =>
  QUESTION_LEAD + Math.max(0, words - 1) * QUESTION_WORD_BEAT + QUESTION_WORD_FADE;
/** Ka on the page but not landing (hidden, or the reader jumped here): the
 *  card still speaks this long after it arrived on screen. */
const LAND_FALLBACK_MS = 1600;

/**
 * Ka's question over his head (Figma 119:8543): the DS tooltip surface. It
 * streams from him, word by word, the moment he lands on his spot (user
 * 2026-09-30) — until then only the empty surface's room is kept, so nothing
 * moves when it arrives. Once said it stays.
 */
function KaQuestion({
  children,
  play,
  reduceMotion,
}: {
  children: string;
  play: boolean;
  reduceMotion: boolean;
}) {
  const words = children.match(/\S+\s*/g) ?? [];
  const shown = play || reduceMotion;
  return (
    <motion.span
      className="flex flex-col items-center"
      initial={false}
      animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : 6 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.25, ease: EASE_OUT }}>
      <span
        className={cn(
          OVERLAY_SURFACE,
          surfaceShape(false),
          OVERLAY_DESC_CLASS,
          'whitespace-nowrap shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)]',
        )}>
        {words.map((word, i) => (
          <motion.span
            key={i}
            initial={reduceMotion ? false : { opacity: 0, filter: 'blur(4px)' }}
            animate={shown ? { opacity: 1, filter: 'blur(0px)' } : undefined}
            transition={{
              duration: QUESTION_WORD_FADE,
              delay: QUESTION_LEAD + i * QUESTION_WORD_BEAT,
              ease: EASE_OUT,
            }}>
            {word}
          </motion.span>
        ))}
      </span>
      <TipArrow
        aria-hidden
        width={TIP_ARROW_SIZE.width}
        height={TIP_ARROW_SIZE.height}
        className="-mt-px"
      />
    </motion.span>
  );
}

/** The file the request on screen acts on (Figma 119:8450): the "sources"
 *  tile's document, or the lecture's slides. It changes only while the
 *  composer is empty, between two requests, and fades in when it does; a
 *  keystroke never re-renders it. */
const ComposerSource = memo(function ComposerSource({
  source,
  reduceMotion,
}: {
  source: AgentSource;
  reduceMotion: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5 px-3 pt-3">
      <motion.span
        key={source}
        initial={reduceMotion ? false : { opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: EASE_OUT }}>
        <Chip size="s" leftIcon={FlashcardIcon} removable className="text-tertiary-400">
          {t(AGENT_SOURCES[source])}
        </Chip>
      </motion.span>
    </div>
  );
});

/** The composer's controls (`+`, `@ Tag`, send, dictation). Static: the
 *  request on screen is read at the press, not rendered in. */
const ComposerControls = memo(function ComposerControls({
  requestRef,
}: {
  /** The request being typed (its `landingV3.agent.prompts` key). */
  requestRef: React.RefObject<string>;
}) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  // Send and dictation are the chat's way in: sign-up (the unified card, as
  // Get Started opens it), or the dashboard for a visitor already signed in.
  const start = (control: 'send' | 'mic') => {
    posthog.capture('Homepage-Agent-Chat-Clicked', {
      pathname: window.location.pathname,
      control,
      request: requestRef.current,
    });
    if (isSignedIn) navigate('/dashboard');
    else authDialogs.openSignUp();
  };
  return (
    <div className="flex items-end gap-2 px-3 pb-3 pt-1">
      {/* The attach and tag buttons stay a picture. */}
      <div aria-hidden inert className="flex min-w-0 items-center">
        <Button
          variant="ghost"
          size="xs"
          icon={Plus}
          accessibilityLabel={t('chat.attach')}
        />
        <Button variant="ghost" size="xs" leftIcon={AtSign}>
          {t('chat.tag')}
        </Button>
      </div>
      <div className="ml-auto flex flex-none items-center gap-2">
        <Button
          variant="secondary"
          size="m"
          icon={SendHorizontal}
          accessibilityLabel={t('chat.send')}
          onPress={() => start('send')}
        />
        <Button
          variant="primary"
          size="m"
          icon={Mic}
          accessibilityLabel={t('chat.dictate')}
          onPress={() => start('mic')}
        />
      </div>
    </div>
  );
});

/**
 * The chat's composer as a picture (Figma 119:8449, the app's `ChatComposer`
 * chrome): the source it acts on, the request being typed and its controls.
 * Out of reach but for send and dictation, which open sign-up. Only the
 * request's text changes with a keystroke.
 */
function DemoComposer({
  text,
  longest,
  source,
  caretSolid,
  near,
  reduceMotion,
  requestRef,
}: {
  text: string;
  /** The longest request: its room is kept, so the card never grows or
   *  shrinks as a request types in and is wiped. */
  longest: string;
  source: AgentSource;
  reduceMotion: boolean;
  requestRef: React.RefObject<string>;
  /** Solid while typing; blinking while the request waits. */
  caretSolid: boolean;
  /** The caret's blink runs only near the screen. */
  near: boolean;
}) {
  return (
    // Sized by the card, never sizing it: the card takes the shortcuts' row.
    <div className={cn(CHAT_COMPOSER_FRAME_CLASS, '[contain:inline-size]')}>
      <div aria-hidden inert>
        <ComposerSource source={source} reduceMotion={reduceMotion} />
      </div>
      {/* `rtl-text`: the global RTL rules pin anything whose class names a
          grid (this <p>, its `[grid-area]` spans) to LTR, which would type the
          Hebrew prompt from the left. */}
      <p
        aria-hidden
        className="rtl-text grid min-h-[3.25rem] px-5 pb-2 pt-4 text-start text-base font-medium text-subtitle-800">
        <span className="invisible [grid-area:1/1]">{longest}</span>
        <span className="[grid-area:1/1]">
          {text}
          <span
            className={cn('lv3-caret bg-primary-500', caretSolid && 'lv3-caret--solid')}
            style={near ? undefined : { animationPlayState: 'paused' }}
          />
        </span>
      </p>
      <ComposerControls requestRef={requestRef} />
    </div>
  );
}

/** The chat's shortcuts (Figma 119:8444): the one standing for the request
 *  on screen is lit. Re-renders only when that changes. */
const ShortcutRow = memo(function ShortcutRow({
  active,
  onPick,
}: {
  active: AgentShortcutId | undefined;
  onPick: (id: AgentShortcutId) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-[5px]">
      {AGENT_SHORTCUTS.map((shortcut) => (
        <Chip
          key={shortcut.id}
          size="l"
          tint={shortcut.id === active ? 'primary' : 'neutral'}
          leftIcon={shortcut.icon}
          // The icon takes the shortcut's hue (the chip paints its icon in its
          // own text colour; the label keeps the tint's).
          className={shortcut.hue}
          onPress={() => onPick(shortcut.id)}
          webProps={{ 'aria-pressed': shortcut.id === active }}>
          {t(shortcut.label)}
        </Chip>
      ))}
    </div>
  );
});

/**
 * The shortcuts and the composer, where the typing lives — its own component,
 * so a keystroke re-renders only the typed text, not Ka's line above it.
 */
function ChatDemo({
  running,
  near,
  reduceMotion,
}: {
  /** The card has arrived and is near the screen. */
  running: boolean;
  near: boolean;
  reduceMotion: boolean;
}) {
  const { t } = useTranslation();
  const requests = AGENT_REQUESTS.map((r) => t(r.promptKey));
  const lengths = useRef<number[]>([]);
  lengths.current = requests.map((r) => r.length);
  const [typing, setTyping] = useTyping(lengths.current, running && !reduceMotion);
  const longest = requests.reduce((a, b) => (b.length > a.length ? b : a), '');
  const request = requests[typing.index] ?? '';
  const typed = reduceMotion ? request : request.slice(0, typing.length);
  const requestRef = useRef('');
  requestRef.current = AGENT_REQUESTS[typing.index]?.key ?? '';
  // A shortcut types the first request it stands for (at once, under reduced
  // motion).
  const pick = useCallback(
    (id: AgentShortcutId) => {
      const index = AGENT_REQUESTS.findIndex((r) => r.shortcut === id);
      setTyping(
        reduceMotion
          ? { index, length: lengths.current[index] ?? 0, phase: 'holding' }
          : typingFrom(index),
      );
    },
    [reduceMotion, setTyping],
  );
  return (
    <>
      <ShortcutRow active={AGENT_REQUESTS[typing.index]?.shortcut} onPick={pick} />
      <DemoComposer
        text={typed}
        longest={longest}
        source={AGENT_REQUESTS[typing.index]?.source ?? 'notes'}
        caretSolid={typing.phase === 'typing'}
        near={near}
        reduceMotion={reduceMotion}
        requestRef={requestRef}
      />
    </>
  );
}

export function AgentChat({
  layout = 'desktop',
  reduceMotion,
}: {
  /** `desktop` = a card as wide as its shortcuts in one row, 740px at least
   *  (the frame's 633 widened for the fifth, user 2026-09-30); `stacked` =
   *  the phone's, full width. */
  layout?: 'desktop' | 'stacked';
  reduceMotion: boolean;
}) {
  const { t } = useTranslation();
  const stacked = layout === 'stacked';
  const ref = useRef<HTMLElement>(null);
  const arrived = useInView(ref, { once: true, amount: 0.3 }) || reduceMotion;
  const near = useInView(ref, { margin: '200px 0px' });
  // The card speaks in turn (user 2026-09-30): Ka lands on his spot, his
  // question streams from him, then his line under it, then the chat's
  // shortcuts and composer arrive and the typing starts. Once.
  const kaHere = useKaLandedOn('agent');
  const [asked, setAsked] = useState(false);
  useEffect(() => {
    if (kaHere || reduceMotion) setAsked(true);
  }, [kaHere, reduceMotion]);
  useEffect(() => {
    if (!arrived || asked) return;
    const id = window.setTimeout(() => setAsked(true), LAND_FALLBACK_MS);
    return () => window.clearTimeout(id);
  }, [arrived, asked]);
  const question = t('landingV3.agent.question');
  const lineSegments = [
    { text: t('landingV3.agent.pre') },
    { text: t('landingV3.agent.hl'), hl: true },
  ];
  const lineDelay = reduceMotion
    ? 0
    : questionSeconds(question.split(/\s+/).filter(Boolean).length) + 0.05;
  const demoDelay = reduceMotion
    ? 0
    : lineDelay + lineStreamSeconds(toWords(lineSegments).length) + 0.1;
  // The typing waits until the composer is there to type into.
  const [demoShown, setDemoShown] = useState(false);
  useEffect(() => {
    if (!asked) return;
    const id = window.setTimeout(() => setDemoShown(true), demoDelay * 1000 + 500);
    return () => window.clearTimeout(id);
  }, [asked, demoDelay]);

  return (
    <section ref={ref} className="relative flex w-full justify-center">
      <div
        className={cn(
          'flex flex-col items-center gap-6',
          stacked
            ? 'w-full max-w-[640px] rounded-b-[32px] rounded-t-[44px] border border-secondary-200 bg-secondary-50/90 p-4 shadow-card-row'
            : cn(
                'w-max min-w-[740px] max-w-full rounded-b-[44px] rounded-t-[60px] p-[25px]',
                GLASS_SHEET_CLASS,
              ),
        )}>
        {/* Ka's corner of the chat (Figma 119:3390): his question, the slot
            he floats into, his line. */}
        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-[13px] text-center',
            stacked ? 'pb-2 pt-8' : 'h-[314px] max-w-[548px]',
          )}>
          <div className="flex flex-col items-center gap-2">
            <KaQuestion play={asked} reduceMotion={reduceMotion}>
              {question}
            </KaQuestion>
            <KaSlot id="agent" size={KA_SMALL} over />
          </div>
          <TriggeredLine
            segments={lineSegments}
            arrived={asked}
            delay={lineDelay}
            reduceMotion={reduceMotion}
            className={cn(
              'font-bold text-secondary-600',
              stacked ? 'text-[24px] leading-[30px]' : 'text-[28px] leading-9',
            )}
          />
        </div>
        <p className="sr-only">{t('landingV3.agent.summary')}</p>
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={asked ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.5, delay: demoDelay, ease: EASE_OUT }}
          className="flex w-full flex-col gap-2">
          <ChatDemo running={demoShown && near} near={near} reduceMotion={reduceMotion} />
        </motion.div>
      </div>
    </section>
  );
}
